import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Trophy,
  Medal,
  Award,
  TrendingUp,
  TrendingDown,
  ArrowUp,
  ArrowDown,
  Layers,
  FileText,
} from "lucide-react";
import { toast } from "sonner";

import LegislationRankings from "@/components/LegislationRankings";
import { LockedPanel } from "@/components/entitlements/LockedPanel";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALL_CATEGORIES = [
  { id: "effectiveness", label: "Effectiveness (LES)", testId: "category-effectiveness", footnote: "Members with no scored activity (no sponsored bill advanced past introduction) have no LES and are omitted from this ranking.",
    desc: "Legislative Effectiveness Score, how well a member moves their bills through committee, passage, and into law." },
  { id: "bills-sponsored", label: "Bills Sponsored", testId: "category-bills-sponsored",
    desc: "Total bills the member has introduced." },
  { id: "bills-passed", label: "Bills Enacted", testId: "category-bills-passed",
    desc: "Sponsored bills that became law." },
  { id: "bipartisan", label: "Bipartisan", testId: "category-bipartisan",
    desc: "How often the member's bills attract cosponsors from the other party." },
  { id: "attendance", label: "Attendance", testId: "category-attendance",
    desc: "Share of roll-call votes the member was present for." },
  { id: "years-in-office", label: "Years in Congress", testId: "category-years-in-office",
    desc: "Total years served in Congress." },
  { id: "age", label: "Age", testId: "category-age",
    desc: "Member's current age." },
  { id: "net-worth", label: "Net Worth", testId: "category-net-worth",
    desc: "Live net-worth estimates from Quiver Quantitative's congressional tracker, built from public financial disclosures (excludes primary residence and liabilities). Members without an estimate rank last; Scorecards exclude them." },
];

const fmtMoney = (v) =>
  v == null
    ? ", "
    : v >= 1e9
    ? `$${(v / 1e9).toFixed(1)}B`
    : v >= 1e6
    ? `$${(v / 1e6).toFixed(1)}M`
    : `$${Math.round(v / 1e3)}K`;

/** Congress picker, 119th is current. We expose the last several congresses
 *  since that's where Congress.gov has reliable roster data and where most
 *  currently-relevant legislators served. Each congress is 2 years starting
 *  in January of an odd year. */
const CONGRESS_OPTIONS = [
  { num: 119, label: "119th", years: "2025-27", current: true },
  { num: 118, label: "118th", years: "2023-25" },
  { num: 117, label: "117th", years: "2021-23" },
  { num: 116, label: "116th", years: "2019-21" },
  { num: 115, label: "115th", years: "2017-19" },
  { num: 114, label: "114th", years: "2015-17" },
];

/** Sports-style persona presets, each is a one-click composite blend.
 *  Pure sugar over the existing chip cycle: applying a preset just rewrites
 *  `selectedCats` to match the preset's direction map, and the existing
 *  composite endpoint does the rest. */
const PERSONAS = [
  {
    id: "legislator",
    label: "The Legislator",
    icon: "📜",
    blurb: "Volden and Wiseman effectiveness: moves bills through every stage of the process",
    cats: [
      { id: "effectiveness", dir: "desc" },
    ],
  },
  {
    id: "veteran",
    label: "The Veteran",
    icon: "🏆",
    blurb: "Long tenure, low output and low effectiveness, coasting on seniority",
    cats: [
      { id: "years-in-office", dir: "desc" },
      { id: "bills-sponsored", dir: "asc" },
      { id: "bills-passed", dir: "asc" },
      { id: "effectiveness", dir: "asc" },
    ],
  },
  {
    id: "rookie",
    label: "The Rookie",
    icon: "🆕",
    blurb: "Young, hungry, prolific freshman",
    cats: [
      { id: "years-in-office", dir: "asc" },
      { id: "age", dir: "asc" },
      { id: "bills-sponsored", dir: "desc" },
    ],
  },
  {
    id: "mvp",
    label: "The MVP",
    icon: "⭐",
    blurb: "Effective in their prime: high LES, laws passed, never misses a vote, works across the aisle",
    cats: [
      { id: "effectiveness", dir: "desc" },
      { id: "bills-passed", dir: "desc" },
      { id: "attendance", dir: "desc" },
      { id: "bipartisan", dir: "desc" },
      { id: "age", dir: "asc" },
    ],
  },
  {
    id: "hall-of-famer",
    label: "The Hall of Famer",
    icon: "🏛️",
    blurb: "Long career, productive, effective, cross-aisle",
    cats: [
      { id: "years-in-office", dir: "desc" },
      { id: "bills-passed", dir: "desc" },
      { id: "effectiveness", dir: "desc" },
      { id: "bipartisan", dir: "desc" },
    ],
  },
  {
    id: "bench-warmer",
    label: "The Backbencher",
    icon: "🪑",
    blurb: "Long tenure with a lighter legislative footprint and a low LES",
    cats: [
      { id: "bills-sponsored", dir: "asc" },
      { id: "bills-passed", dir: "asc" },
      { id: "effectiveness", dir: "asc" },
      { id: "attendance", dir: "asc" },
      { id: "years-in-office", dir: "desc" },
    ],
  },
  {
    id: "free-agent",
    label: "The Free Agent",
    icon: "🤝",
    blurb: "Cross-aisle dealmaker who never misses a vote",
    cats: [
      { id: "bipartisan", dir: "desc" },
      { id: "attendance", dir: "desc" },
    ],
  },
];

const Rankings = () => {
  // selectedCats: ordered list of {id, dir} where dir is "desc" (high/best) or
  // "asc" (low/worst). Each chip cycles: off → desc → asc → off.
  // 1 entry = single-category leaderboard. 2+ = composite mode where each
  // category's normalized score is direction-aware before averaging, so the
  // user can blend "lowest bills sponsored + lowest bills passed + highest
  // years in congress" into a single ranking.
  const { hasFeature } = useAuth();
  const [selectedCats, setSelectedCats] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [chamber, setChamber] = useState("all");
  const [party, setParty] = useState("all");
  const [congressNum, setCongressNum] = useState(CONGRESS_OPTIONS[0].num);
  const [loading, setLoading] = useState(false);
  const [upgradeLock, setUpgradeLock] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  // "legislators" (original people-based leaderboard) OR "legislation"
  // (bills ranked by critical / contentious / stalled / recent / …).
  const [mode, setMode] = useState("legislators");

  const isMulti = selectedCats.length >= 2;
  const sigKey = selectedCats.map((c) => `${c.id}:${c.dir}`).join(",");
  // Monotonic request id, rapid chip clicks fire overlapping requests and a
  // slow earlier response must never overwrite a newer one (users saw stale
  // "age HIGH" results after flipping the chip to LOW mid-flight).
  const fetchSeq = useRef(0);

  useEffect(() => {
    fetchRankings();
  }, [sigKey, chamber, party, congressNum]); // eslint-disable-line

  const fetchRankings = async () => {
    if (selectedCats.length === 0) {
      // Blank Scorecard, user is building from scratch; clear stale results.
      fetchSeq.current += 1; // invalidate any in-flight request
      setRankings([]);
      setLoading(false);
      return;
    }
    const seq = ++fetchSeq.current;
    try {
      setLoading(true);
      setUpgradeLock(null);
      setFetchError(false);
      const params = new URLSearchParams();
      if (chamber !== "all") params.set("chamber", chamber);
      if (party !== "all") params.set("party", party);
      // Only send `congress` for non-current sessions, keeps the default
      // path identical to before (backend resolves null → CURRENT_CONGRESS).
      if (congressNum !== CONGRESS_OPTIONS[0].num) params.set("congress", String(congressNum));
      // Show enough legislators that notable figures don't drop off the list.
      // Backend default is 20, which hid Sen. Booker at #21 on bills-sponsored.
      params.set("limit", "50");
      let url;
      if (isMulti) {
        params.set("categories", selectedCats.map((c) => `${c.id}:${c.dir}`).join(","));
        params.set("order", "desc"); // composite always returns best-fit first
        url = `${API}/legislators/rankings/composite?${params.toString()}`;
      } else {
        params.set("order", selectedCats[0].dir);
        url = `${API}/legislators/rankings/${selectedCats[0].id}?${params.toString()}`;
      }
      const response = await axios.get(url);
      if (seq !== fetchSeq.current) return; // stale response, a newer request is in flight
      setRankings(response.data);
    } catch (error) {
      if (seq !== fetchSeq.current) return;
      const body = error.response?.data;
      if (error.response?.status === 403 && (body?.error === "upgrade_required" || body?.detail === "upgrade_required")) {
        setRankings([]);
        setUpgradeLock({ feature: body.feature || "fit_scorecards", tier: body.required_tier || "intelligence" });
      } else {
        // Never leave a stale (differently-filtered) list on screen.
        setRankings([]);
        setFetchError(true);
        toast.error("Failed to load rankings");
      }
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  /** Cycle a category chip through 3 states:
   *    not selected → desc (high/best)  → asc (low/worst) → not selected
   *  Always keeps at least 1 chip selected so the page can render.
   */
  const cycleCategory = (catId) => {
    setSelectedCats((prev) => {
      const idx = prev.findIndex((c) => c.id === catId);
      if (idx < 0) {
        return [...prev, { id: catId, dir: "desc" }];
      }
      const cur = prev[idx];
      if (cur.dir === "desc") {
        const next = [...prev];
        next[idx] = { id: catId, dir: "asc" };
        return next;
      }
      // dir === "asc" → remove (unless it's the only selection)
      if (prev.length === 1) {
        // Don't unselect the last one, re-cycle to desc
        return [{ id: catId, dir: "desc" }];
      }
      return prev.filter((c) => c.id !== catId);
    });
  };

  const getPartyColor = (party) => {
    const colors = {
      D: "bg-[#2F6BFF]/20 text-blue-300 border-[#2F6BFF]/40",
      R: "bg-[#FF3B3B]/20 text-rose-300 border-[#FF3B3B]/40",
      I: "bg-purple-500/20 text-purple-300 border-purple-500/40",
    };
    return colors[party] || "bg-white/10 text-white/70 border-white/20";
  };

  // In single-category mode, the medal row reflects the chip's direction.
  const primaryDir = selectedCats[0]?.dir || "desc";

  const getRankMedal = (rank) => {
    if (primaryDir === "desc" || isMulti) {
      if (rank === 1) return <Trophy className="w-8 h-8 text-orange-400" />;
      if (rank === 2) return <Medal className="w-8 h-8 text-white/60" />;
      if (rank === 3) return <Award className="w-8 h-8 text-amber-500" />;
    }
    return <span className="text-2xl font-bold text-white/35">#{rank}</span>;
  };

  const getCategoryLabel = (cat) => ALL_CATEGORIES.find((c) => c.id === cat)?.label || cat;

  const renderCategoryValue = (legislator, cat) => {
    switch (cat) {
      case "bills-sponsored":
        return legislator.stats?.billsSponsored ?? 0;
      case "bills-passed":
        return legislator.stats?.billsPassed ?? 0;
      case "bipartisan":
        return Number(legislator.stats?.bipartisanIndex || 0).toFixed(1);
      case "attendance":
        return legislator.stats?.votesCount
          ? `${((legislator.stats.votesCast / legislator.stats.votesCount) * 100).toFixed(1)}%`
          : "n/a";
      case "years-in-office":
        return legislator.yearsInOffice ?? "n/a";
      case "age":
        return legislator.age != null ? `${legislator.age}` : "n/a";
      case "net-worth":
        return fmtMoney(legislator.netWorth?.midpointUSD);
      case "effectiveness": {
        const score = legislator.stats?.effectivenessScore;
        if (score == null || score === 0) return "n/a";
        const dir = legislator.stats?.lesTrend?.direction;
        return (
          <span className="inline-flex items-center gap-2">
            {Number(score).toFixed(2)}
            {dir === "rising" && (
              <span
                data-testid={`les-trend-badge-${legislator.id}`}
                title={`LES up ${legislator.stats.lesTrend.delta} since ${legislator.stats.lesTrend.from}`}
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
              >
                <TrendingUp className="w-3 h-3" /> Rising
              </span>
            )}
            {dir === "falling" && (
              <span
                data-testid={`les-trend-badge-${legislator.id}`}
                title={`LES down ${legislator.stats.lesTrend.delta} since ${legislator.stats.lesTrend.from}`}
                className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-400 border border-rose-500/30"
              >
                <TrendingDown className="w-3 h-3" /> Falling
              </span>
            )}
          </span>
        );
      }
      default:
        return "n/a";
    }
  };

  const headerTitle = isMulti
    ? "Scorecard Rankings"
    : primaryDir === "desc"
    ? "Legislator Rankings"
    : "Legislator Reverse Rankings";
  const headerSubtitle = isMulti
    ? `Your custom Scorecard blends ${selectedCats.length} categories. Each chip's ↑ HIGH or ↓ LOW tells WHIP what "best" means for that metric. 100 = perfect match to your criteria, 0 = furthest from it. #1 on the list is the closest match, regardless of whether you picked highs or lows.`
    : primaryDir === "desc"
    ? "Performance leaderboards, highest first. Click the chip again to flip to lowest first."
    : "Reverse leaderboards, lowest first. Useful for spotting under-performers or the least partisan members.";

  return (
    <div className="min-h-screen" style={{ background: "var(--whip-bg)" }}>
      <Navigation />

      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3">
            {mode === "legislation" ? (
              <FileText className="w-10 h-10 text-orange-400" />
            ) : isMulti ? (
              <Layers className="w-10 h-10 text-orange-400" />
            ) : primaryDir === "desc" ? (
              <Trophy className="w-10 h-10 text-orange-400" />
            ) : (
              <TrendingDown className="w-10 h-10 text-rose-400" />
            )}
            <div>
              <h1 className="text-4xl font-black tracking-tight text-white" data-testid="rankings-title">
                {mode === "legislation" ? "Legislation Rankings" : headerTitle}
              </h1>
              <p className="text-sm text-white/55 mt-1 max-w-3xl">
                {mode === "legislation"
                  ? "Rank bills by must-pass criticality, contention, stalled status, and recency. Ideal for spotting government-shutdown risks, flashpoint debates, and bills quietly dying in committee."
                  : headerSubtitle}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Legislators ↔ Legislation mode toggle */}
        <div className="mb-6" data-testid="rankings-mode-toggle">
          <div
            role="tablist"
            className="inline-flex p-1 rounded-xl border border-white/10"
            style={{ background: "rgba(255,255,255,0.03)" }}
          >
            {[
              { id: "legislators", label: "Legislator Rankings", icon: Trophy, desc: "Rank the people" },
              { id: "legislation", label: "Legislation Rankings", icon: FileText, desc: "Rank the bills" },
            ].map((m) => {
              const active = mode === m.id;
              const Icon = m.icon;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`rankings-mode-${m.id}`}
                  onClick={() => setMode(m.id)}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                    active
                      ? "text-white"
                      : "text-white/55 hover:text-white/85"
                  }`}
                  style={
                    active
                      ? {
                          background:
                            "linear-gradient(135deg, rgba(255,106,0,0.25), rgba(255,165,0,0.15))",
                          boxShadow:
                            "inset 0 0 0 1px rgba(255,138,0,0.4), 0 0 16px rgba(255,138,0,0.15)",
                        }
                      : {}
                  }
                >
                  <Icon className="w-4 h-4" />
                  <span>{m.label}</span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-white/45 mt-2 ml-1">
            {mode === "legislators"
              ? "Rank members of Congress by sponsorship, attendance, tenure, and more."
              : "Rank bills by must-pass criticality, contention, activity, and status."}
          </p>
        </div>

        {mode === "legislation" ? (
          <LegislationRankings congressNum={congressNum} />
        ) : (
          <>
        {/* Persona presets, sports-themed one-click composite blends */}
        <div className="mb-6">
          <div className="flex items-baseline justify-between mb-3">
            <label className="text-sm font-medium text-white/80">
              Personas
              <span className="text-xs text-white/45 ml-2">
                One-click Scorecard blends, the leaderboard shows who matches the archetype best. Persona picks stay as chips below; tweak them, or hit "Start blank" to clear everything.
              </span>
            </label>
          </div>
          <div className="grid gap-2 grid-cols-2 md:grid-cols-4 xl:grid-cols-7" data-testid="persona-presets">
            {PERSONAS.map((p) => {
              const active =
                selectedCats.length === p.cats.length &&
                p.cats.every(
                  (c) => selectedCats.find((s) => s.id === c.id && s.dir === c.dir)
                );
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedCats(hasFeature("legislative_effectiveness") ? p.cats : p.cats.filter((c) => c.id !== "effectiveness"))}
                  data-testid={`persona-${p.id}`}
                  aria-pressed={active}
                  title={p.blurb}
                  className={`text-left rounded-lg border p-2.5 transition-all ${
                    active
                      ? "border-orange-500 bg-orange-500/10 whip-glow"
                      : "border-white/10 bg-white/[0.03] hover:border-orange-500/40 hover:bg-white/[0.05]"
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-sm" aria-hidden>{p.icon}</span>
                    <span className={`text-[13px] font-bold tracking-tight truncate ${active ? "text-orange-200" : "text-white"}`}>
                      {p.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/55 leading-snug line-clamp-2">{p.blurb}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white/80">
                Categories
                <span className="text-xs text-white/45 ml-2">
                  Click a chip to add it (default ↑ HIGH). Click again to flip to ↓ LOW. Third click removes it.
                </span>
              </label>
              <div className="flex items-center gap-3">
                {selectedCats.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedCats([])}
                    className="text-xs text-white/50 hover:text-white border border-white/15 hover:border-orange-500/50 rounded-full px-2.5 py-1 transition-colors"
                    data-testid="rankings-start-blank"
                    title="Remove all chips (including persona picks) and build a Scorecard from scratch"
                  >
                    Start blank
                  </button>
                )}
                {isMulti && (
                  <button
                    type="button"
                    onClick={() => setSelectedCats([selectedCats[0]])}
                    className="text-xs text-orange-300 hover:text-orange-200"
                    data-testid="rankings-clear-multi"
                  >
                    Clear extra categories
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="category-multiselect">
              {ALL_CATEGORIES.map((c) => {
                const sel = selectedCats.find((s) => s.id === c.id);
                const active = !!sel;
                const dir = sel?.dir;
                let chipClass = "bg-white/5 text-white/70 border-white/15 hover:border-orange-500/40 hover:text-white";
                if (active && dir === "desc") {
                  chipClass = "whip-orange-bg text-white border-orange-500 shadow-[0_0_0_1px_rgba(255,138,0,0.4),0_0_18px_rgba(255,138,0,0.25)]";
                } else if (active && dir === "asc") {
                  chipClass = "bg-rose-600 text-white border-rose-500 shadow-[0_0_0_1px_rgba(244,63,94,0.4),0_0_18px_rgba(244,63,94,0.25)]";
                }
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={c.testId}
                    data-dir={dir || "off"}
                    onClick={() => cycleCategory(c.id)}
                    className={`text-xs font-semibold px-3 py-2 rounded-full border transition-all flex items-center gap-1.5 ${chipClass}`}
                    aria-pressed={active}
                    title={`${c.desc}\n${!active ? "Click to add" : dir === "desc" ? "↑ Highest first, click to flip to lowest" : "↓ Lowest first, click to remove"}`}
                  >
                    {c.label}
                    {active && dir === "desc" && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-black tracking-wide bg-black/25 rounded px-1 py-0.5">
                        <ArrowUp className="w-3 h-3" /> HIGH
                      </span>
                    )}
                    {active && dir === "asc" && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-black tracking-wide bg-black/25 rounded px-1 py-0.5">
                        <ArrowDown className="w-3 h-3" /> LOW
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {upgradeLock && (
            <LockedPanel
              feature={upgradeLock.feature}
              tier={upgradeLock.tier}
              title="Custom Scorecard Blends"
              blurb="Persona presets are included in your plan. Building your own category blends with custom HIGH/LOW directions requires WHIP Intelligence."
              className="mb-6"
            />
          )}
          {fetchError && !loading && (
            <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200" data-testid="rankings-error">
              <span>Couldn't load rankings for these filters, the server may be warming up.</span>
              <button onClick={fetchRankings} className="rounded-lg border border-rose-400/40 px-3 py-1 text-xs font-semibold hover:bg-rose-500/20" data-testid="rankings-retry">Retry</button>
            </div>
          )}

          <div className="flex flex-wrap gap-6">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Chamber</label>
              <Tabs value={chamber} onValueChange={setChamber}>
                <TabsList className="inline-flex gap-2">
                  <TabsTrigger data-testid="chamber-all" value="all" className="data-[state=active]:bg-white/15 data-[state=active]:text-white text-white/65">
                    All
                  </TabsTrigger>
                  <TabsTrigger data-testid="chamber-senate" value="Senate" className="data-[state=active]:bg-white/15 data-[state=active]:text-white text-white/65">
                    Senate
                  </TabsTrigger>
                  <TabsTrigger data-testid="chamber-house" value="House" className="data-[state=active]:bg-white/15 data-[state=active]:text-white text-white/65">
                    House
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Party</label>
              <div className="inline-flex gap-2" data-testid="party-filter">
                {[
                  { id: "all", label: "All", color: "bg-white/15 border-white/25" },
                  { id: "D", label: "Democrat", color: "bg-[#2F6BFF] border-[#2F6BFF]" },
                  { id: "R", label: "Republican", color: "bg-[#FF3B3B] border-[#FF3B3B]" },
                  { id: "I", label: "Independent", color: "bg-purple-500 border-purple-500" },
                ].map((p) => {
                  const active = party === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`party-${p.id}`}
                      onClick={() => setParty(p.id)}
                      className={`text-xs font-semibold px-3 py-2 rounded-md border transition-all flex items-center gap-1.5 ${
                        active
                          ? `${p.color} text-white shadow-[0_0_0_1px_rgba(255,255,255,0.15)]`
                          : "bg-white/5 text-white/65 border-white/15 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      {p.id !== "all" && (
                        <span
                          className={`inline-block w-2 h-2 rounded-full ${
                            p.id === "D" ? "bg-[#2F6BFF]" : p.id === "R" ? "bg-[#FF3B3B]" : "bg-purple-400"
                          } ${active ? "opacity-0" : ""}`}
                        />
                      )}
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-2 flex items-center gap-2">
                Congress
                <span className="text-[10px] font-normal text-white/45 normal-case tracking-normal">
                  Roster only, stats are cross-session
                </span>
              </label>
              <div className="inline-flex flex-wrap gap-2" data-testid="congress-filter">
                {CONGRESS_OPTIONS.map((c) => {
                  const active = congressNum === c.num;
                  return (
                    <button
                      key={c.num}
                      type="button"
                      data-testid={`congress-${c.num}`}
                      onClick={() => setCongressNum(c.num)}
                      title={`${c.label} Congress · ${c.years}${c.current ? " (current session)" : ""}`}
                      className={`text-xs font-semibold px-3 py-2 rounded-md border transition-all flex items-center gap-1.5 ${
                        active
                          ? "whip-orange-bg text-white border-orange-400 shadow-[0_0_0_1px_rgba(255,138,0,0.4)]"
                          : "bg-white/5 text-white/65 border-white/15 hover:border-orange-400/40 hover:text-white"
                      }`}
                    >
                      {c.label}
                      {c.current && (
                        <span className={`text-[9px] uppercase tracking-wider ${active ? "text-white/85" : "text-orange-300/80"}`}>
                          · live
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-white/55">Loading rankings...</p>
          </div>
        )}

        {!loading && selectedCats.length > 0 && (
          <div className="space-y-4">
            {!isMulti && ALL_CATEGORIES.find((c) => c.id === selectedCats[0]?.id)?.footnote && (
              <p className="text-xs text-white/45 border border-white/10 rounded-lg px-3 py-2 bg-white/[0.02]" data-testid="rankings-footnote">
                {ALL_CATEGORIES.find((c) => c.id === selectedCats[0]?.id).footnote}
              </p>
            )}
            {rankings.map((legislator, index) => {
              const composite = legislator.composite;
              const primaryValue = isMulti
                ? composite?.score?.toFixed(1)
                : renderCategoryValue(legislator, selectedCats[0].id);
              const primaryLabel = isMulti ? "Scorecard" : getCategoryLabel(selectedCats[0].id);
              // In multi-mode, the leaderboard is always "best fit first", so
              // the top-3 always get the orange medal accent. In single mode
              // we use the chip's direction to decide medal vs reverse-rank.
              const topAccent = isMulti || primaryDir === "desc";
              return (
                <Link key={legislator.id} to={`/legislator/${legislator.id}`} className="block">
                <Card
                  data-testid={`rank-card-${index + 1}`}
                  className={`p-6 hover:shadow-xl transition-all duration-300 border ${
                    topAccent && index < 3
                      ? "border-orange-500/40 whip-glow"
                      : !topAccent && index < 3
                      ? "border-rose-500/40"
                      : "border-white/10"
                  } rounded-xl cursor-pointer`}
                  style={{
                    background:
                      topAccent && index < 3
                        ? "linear-gradient(135deg, rgba(255,138,0,0.08), rgba(11,11,15,0.95))"
                        : !topAccent && index < 3
                        ? "linear-gradient(135deg, rgba(255,59,59,0.08), rgba(11,11,15,0.95))"
                        : "var(--whip-surface)",
                  }}
                >
                  <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-6">
                    {/* Rank */}
                    <div className="flex-shrink-0 w-10 sm:w-16 flex items-center justify-center">
                      {getRankMedal(index + 1)}
                    </div>

                    {/* Photo */}
                    <div className="relative flex-shrink-0">
                      <img
                        src={legislator.photo}
                        alt={legislator.name}
                        className="w-14 h-14 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-white/15"
                      />
                      <div
                        className={`absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 rounded-full ${getPartyColor(
                          legislator.party
                        )} border-2 border-[#0B0B0F] flex items-center justify-center text-xs font-bold`}
                      >
                        {legislator.party}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg sm:text-2xl font-bold text-white mb-1">{legislator.name}</h3>
                      <p className="text-sm text-white/55">
                        {legislator.state} • {legislator.chamber} • {legislator.yearsInOffice} years
                      </p>
                    </div>

                    {/* Score */}
                    <div className="shrink-0 basis-full sm:basis-auto flex items-center justify-between sm:block text-right border-t border-white/5 sm:border-0 pt-3 sm:pt-0">
                      <div className="flex items-center gap-2 mb-0 sm:mb-1 justify-end">
                        {topAccent ? (
                          <TrendingUp className="w-5 h-5 text-orange-400" />
                        ) : (
                          <TrendingDown className="w-5 h-5 text-rose-400" />
                        )}
                        <p className="text-xs uppercase tracking-wider text-white/45">{primaryLabel}</p>
                      </div>
                      <p className={`text-3xl sm:text-4xl font-black tabular-nums ${topAccent ? "whip-orange-text" : "text-rose-400"}`}>
                        {primaryValue}
                      </p>
                      {!isMulti && selectedCats[0]?.id === "net-worth" && legislator.netWorth?.source && (
                        <p className="text-[10px] text-white/45 mt-1" data-testid={`net-worth-source-${index + 1}`}>
                          {legislator.netWorth.source}
                          {legislator.netWorth.asOfYear ? ` · ${legislator.netWorth.asOfYear}` : ""}
                        </p>
                      )}
                      {isMulti && (
                        <p className="text-[10px] text-white/45 uppercase tracking-wider mt-1">
                          Higher = better match
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Composite breakdown, only in multi-select mode */}
                  {isMulti && composite && (
                    <div
                      className="mt-4 pt-4 border-t border-white/10"
                      data-testid={`composite-breakdown-${index + 1}`}
                    >
                      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${selectedCats.length}, minmax(0, 1fr))` }}>
                        {selectedCats.map(({ id: cat, dir }) => {
                          const raw = composite.values?.[cat];
                          const isAsc = dir === "asc";
                          const matchPct = Math.round(composite.adjusted?.[cat] ?? 0);
                          const tier = matchPct >= 70
                            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                            : matchPct >= 40
                            ? "bg-white/10 text-white/60 border-white/15"
                            : "bg-rose-500/15 text-rose-300 border-rose-500/30";
                          return (
                            <div
                              key={cat}
                              className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5"
                              title={`Matches your ${isAsc ? "↓ LOW" : "↑ HIGH"} pick better than ${matchPct}% of members`}
                              data-testid={`breakdown-tile-${cat}-${index + 1}`}
                            >
                              <p className="text-[10px] text-white/50 uppercase tracking-wider flex items-center gap-1">
                                {getCategoryLabel(cat)}
                                {isAsc ? <ArrowDown className="w-3 h-3 text-rose-400" /> : <ArrowUp className="w-3 h-3 text-orange-400" />}
                              </p>
                              <div className="flex items-baseline justify-between gap-2 mt-1">
                                <span className="text-xl font-bold text-white tabular-nums">
                                  {raw == null ? ", " : renderCategoryValue(legislator, cat)}
                                </span>
                                <span className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded-full border shrink-0 ${tier}`}>
                                  {matchPct}% match
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Single-mode stats bar (preserved for backward compat) */}
                  {!isMulti && (
                    <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-5 gap-4 text-center">
                      <div>
                        <p className="text-xs text-white/45">Bills Sponsored</p>
                        <p className="text-lg font-semibold text-white">{legislator.stats?.billsSponsored}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/45">Bills Enacted</p>
                        <p className="text-lg font-semibold text-white">{legislator.stats?.billsPassed}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/45">Voting Record</p>
                        <p className="text-lg font-semibold text-white">
                          {legislator.stats?.votesCount
                            ? `${((legislator.stats.votesCast / legislator.stats.votesCount) * 100).toFixed(1)}%`
                            : "n/a"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-white/45">Bipartisan Index</p>
                        <p className="text-lg font-semibold text-white">{Number(legislator.stats?.bipartisanIndex || 0).toFixed(1)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-white/45">Years in Congress</p>
                        <p className="text-lg font-semibold text-white">{legislator.yearsInOffice ?? "n/a"}</p>
                      </div>
                    </div>
                  )}
                </Card>
                </Link>
              );
            })}
          </div>
        )}

        {!loading && rankings.length === 0 && selectedCats.length > 0 && (
          <div className="text-center py-12 text-white/55">No legislators match the selected filters.</div>
        )}

        {!loading && selectedCats.length === 0 && (
          <div
            className="text-center py-16 rounded-xl border border-dashed border-white/15 bg-white/[0.02]"
            data-testid="rankings-blank-state"
          >
            <p className="text-lg font-bold text-white mb-2">Blank Scorecard</p>
            <p className="text-sm text-white/55 max-w-md mx-auto">
              Pick one or more category chips above, each click adds it as{" "}
              <span className="text-orange-300 font-semibold">↑ HIGH</span>, a second click flips it to{" "}
              <span className="text-rose-300 font-semibold">↓ LOW</span>. Or grab a persona preset to start
              from an archetype.
            </p>
          </div>
        )}
          </>
        )}
      </main>
    </div>
  );
};

export default Rankings;
